import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import api, { apiErr, fmtMoney } from "../lib/api";
import { Loader2, Heart, Gift } from "lucide-react";
import PageHeader from "../components/PageHeader";
import GiveDialog from "../components/GiveDialog";
import { openGive } from "../lib/give";
import { useAuth } from "../context/AuthContext";

export default function Impact() {
  const { config, org } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [giveOpen, setGiveOpen] = useState(false);
  const giveUrl = config?.donate_url || (org?.slug === "reset" ? "https://akop.live/give" : "");
  useEffect(() => {
    (async () => {
      try { const r = await api.get("/impact"); setData(r.data); }
      catch (e) { toast.error(apiErr(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin accent-text" /></div>;
  const cur = data?.currency || "USD";

  return (
    <div>
      <PageHeader title="Your Impact" />
      <div className="rounded-3xl p-7 text-white shadow-[0_8px_32px_rgba(44,44,62,0.12)]" style={{ background: "linear-gradient(135deg,var(--brand-navy),var(--brand-navy-dark))" }} data-testid="impact-card">
        <Heart className="h-6 w-6 mb-3 accent-text" fill="currentColor" />
        <div className="text-sm opacity-80">You've given</div>
        <div className="font-heading font-extrabold text-5xl mt-1">{fmtMoney(data?.total_given_cents, cur)}</div>
        <div className="text-sm opacity-80 mt-2">across {data?.gift_count || 0} {data?.gift_count === 1 ? "gift" : "gifts"} — thank you for making this mission possible.</div>
        {giveUrl && (
          <button data-testid="impact-give-again-btn" onClick={() => openGive(giveUrl)}
            className="mt-5 inline-flex items-center gap-2 bg-white text-[var(--brand-navy)] font-semibold rounded-full px-5 py-2.5 text-sm transition-transform hover:scale-[1.03]">
            <Gift className="h-4 w-4" /> Give again
          </button>
        )}
      </div>
      <GiveDialog open={giveOpen} onOpenChange={setGiveOpen} donateUrl={config?.donate_url} donateLabel={config?.donate_label} orgSlug={org?.slug} />

      <h2 className="font-heading font-bold text-lg text-[#2C2C3E] mt-7 mb-3">Your giving history</h2>
      <div className="space-y-2">
        {(!data?.gifts || data.gifts.length === 0) && <p className="text-sm text-muted-foreground">No gifts recorded yet. Your leader will log gifts here.</p>}
        {data?.gifts?.map((g) => (
          <div key={g.id} className="bg-white rounded-xl p-4 flex items-center justify-between shadow-[0_4px_24px_rgba(44,44,62,0.04)]" data-testid="gift-history-item">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-[var(--tenant-accent)]/15 flex items-center justify-center"><Gift className="h-4 w-4 accent-text" /></div>
              <div>
                <div className="text-sm font-medium text-[#2C2C3E]">{g.method}</div>
                <div className="text-xs text-muted-foreground">{g.date ? format(new Date(g.date), "MMM d, yyyy") : ""}</div>
              </div>
            </div>
            <span className="font-heading font-bold text-[var(--brand-navy)]">{fmtMoney(g.amount_cents, g.currency || cur)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
